import { useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import { toast } from "sonner";
import { Sparkles, FileText, Download } from "lucide-react";

export default function ContractDraft() {
  const { coachId } = useParams();
  const [params] = useSearchParams();
  const jobId = params.get("job");
  const nav = useNavigate();
  const [offerSalary, setOfferSalary] = useState(85000);
  const [years, setYears] = useState(3);
  const [startDate, setStartDate] = useState("");
  const [terms, setTerms] = useState("Performance bonuses tied to conference standing and team APR. Housing stipend negotiable.");
  const [contract, setContract] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!jobId) return toast.error("Job context missing. Open this page from a job's candidate list.");
    setBusy(true);
    try {
      const { data } = await api.post("/contracts/generate", {
        job_id: jobId,
        coach_profile_id: coachId,
        offer_salary: Number(offerSalary),
        contract_years: Number(years),
        start_date: startDate || null,
        additional_terms: terms || null,
      });
      setContract(data);
      toast.success("Contract draft ready.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Contract failed");
    } finally { setBusy(false); }
  };

  const printContract = () => window.print();

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-4xl" data-testid="contract-draft-page">
        <div className="text-xs tracking-widest text-blaze">CONTRACTAGENT</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">DRAFT OFFER CONTRACT</h1>
        <p className="text-neutral-400 mt-2 max-w-xl">Tuned to the program's needs and the coach's preferences. Editable plain-English clauses.</p>

        {!contract && (
          <div className="mt-6 border border-neutral-800 p-6 bg-appcard grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Offer salary (USD)"><input type="number" className="input" data-testid="ct-salary" value={offerSalary} onChange={(e) => setOfferSalary(e.target.value)} /></Field>
            <Field label="Contract years"><input type="number" min={1} max={10} className="input" data-testid="ct-years" value={years} onChange={(e) => setYears(e.target.value)} /></Field>
            <Field label="Start date (optional)"><input type="date" className="input" data-testid="ct-start" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></Field>
            <div className="md:col-span-2">
              <Field label="Additional terms (bonuses, housing, camps, NIL collective, etc.)"><textarea className="input min-h-[80px]" data-testid="ct-terms" value={terms} onChange={(e) => setTerms(e.target.value)} /></Field>
            </div>
            <div className="md:col-span-2">
              <button onClick={generate} disabled={busy || !jobId} data-testid="ct-generate-btn" className="w-full bg-blaze text-white heading text-lg py-3 hover:glow-blaze transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                <Sparkles size={16} /> {busy ? "DRAFTING…" : "GENERATE CONTRACT DRAFT"}
              </button>
              {!jobId && <p className="text-xs text-neutral-500 mt-2">Open this from a job's candidate match list to attach job context.</p>}
            </div>
          </div>
        )}

        {contract && (
          <div className="mt-8 bg-white text-neutral-900 print:bg-white" id="contract-print" data-testid="contract-output">
            <div className="px-8 py-10 max-w-3xl mx-auto">
              <div className="text-xs tracking-widest text-blaze">DRAFT · NOT LEGAL ADVICE</div>
              <h2 className="font-bold text-3xl mt-2">{contract.draft.title}</h2>
              <p className="mt-4 text-neutral-700">{contract.draft.preamble}</p>
              <div className="mt-6 space-y-4">
                {contract.draft.sections?.map((s, i) => (
                  <div key={i} className="border-b border-neutral-200 pb-3">
                    <div className="font-bold text-base uppercase tracking-wider">{s.heading}</div>
                    <p className="text-neutral-800 mt-1 leading-relaxed text-sm whitespace-pre-line">{s.body}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 grid grid-cols-2 gap-8">
                {contract.draft.signatures?.map((sig, i) => (
                  <div key={i}>
                    <div className="border-b border-neutral-900 h-10" />
                    <div className="text-xs mt-1 text-neutral-700">{sig}</div>
                    <div className="text-[10px] text-neutral-500 mt-1">Date: __________________</div>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-[10px] text-neutral-500">
                Draft generated by CoachConnect AI ContractAgent. Review with legal counsel before execution.
              </div>
            </div>
            <div className="px-8 py-4 bg-neutral-100 border-t border-neutral-200 flex gap-2 print:hidden">
              <button onClick={printContract} className="bg-neutral-900 text-white px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2" data-testid="ct-print-btn">
                <Download size={14} /> Print / Save PDF
              </button>
              <button onClick={() => setContract(null)} className="border border-neutral-300 text-neutral-700 px-4 py-2 text-xs uppercase tracking-widest" data-testid="ct-regenerate-btn">
                Edit terms & regenerate
              </button>
              <button onClick={() => nav(`/coaches/${coachId}?job=${jobId}`)} className="ml-auto border border-neutral-300 text-neutral-700 px-4 py-2 text-xs uppercase tracking-widest" data-testid="ct-back-btn">
                Back to dossier
              </button>
            </div>
          </div>
        )}
      </div>
      <style>{`.input { width: 100%; background: #121212; border: 1px solid #27272A; padding: 10px 12px; color: white; outline: none; font-size: 13px; }
      .input:focus { border-color: #FF3B30; }
      @media print {
        body { background: white; }
        .print\\:hidden { display: none !important; }
        header, footer { display: none !important; }
      }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="text-[10px] tracking-widest text-neutral-500 mb-1.5">{label.toUpperCase()}</div>{children}</label>;
}
